import { useEffect, useRef, useState, type ReactNode } from "react";

type Props = {
  children: ReactNode;
  className?: string;
  /** rise: fade + slide up (default). scale: fade + slight scale, for stages and mocks. */
  variant?: "rise" | "scale";
  /** Animate direct children one after another. */
  stagger?: boolean;
  delay?: number;
};

/** Scroll-triggered reveal. Runs once, when ~15% of the block is visible; respects prefers-reduced-motion via CSS. */
export function Reveal({ children, className = "", variant = "rise", stagger = false, delay = 0 }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || shown) return;
    if (typeof IntersectionObserver === "undefined") { setShown(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.15, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [shown]);
  return (
    <div ref={ref} style={delay ? { transitionDelay: `${delay}ms` } : undefined}
      className={`fs-reveal fs-reveal-${variant} ${stagger ? "fs-reveal-stagger" : ""} ${shown ? "is-shown" : ""} ${className}`}>
      {children}
    </div>
  );
}
